import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, ArrowUpRight, ChevronDown, Code as CodeIcon, PanelBottom, PanelRight, RotateCw, Ruler, SlidersHorizontal } from 'lucide-react';
import { Button } from '../shared/Button.jsx';
import { DescribeState } from './DescribeState.jsx';
import { ScrollArea } from '../shared/ScrollArea.jsx';
import { Controls, ControlsActions } from '../shared/controls/Controls.jsx';
import { Code } from '../shared/code/Code.jsx';
import { DesignPill } from '../shared/design.jsx';
import { Dropdown } from '../shared/controls/Dropdown.jsx';
import { Tabs, TabsList, TabsPanel, TabsTab } from '../shared/Tabs.jsx';
import { ToggleGroup, ToggleGroupItem } from '../shared/ToggleGroup.jsx';
import { Tooltip } from '../shared/Tooltip.jsx';
import { DocToolbar } from '../shared/Toolbar.jsx';
import { SwapText } from '../shared/Swap.jsx';
import { useDelayed } from '../shared/useDelayed.js';

const VIEWPORTS = [
  { id: 'fit', label: 'Fit', width: null },
  { id: 'mobile', label: '390', width: 390 },
  { id: 'tablet', label: '768', width: 768 },
  { id: 'desktop', label: '1280', width: 1280 },
];
const MIN_WIDTH = 240;
// Side grows in width, bottom in height. Each is remembered on its own.
// Below the minimum the controls no longer fit; the canvas keeps the rest.
const PANEL_MIN = { right: 280, bottom: 220 };
const PANEL_MAX_WIDTH = 768;
const PANEL_CANVAS_MIN = 240;
// Controls and Code share the panel: stacked on the side, side by side at the
// bottom once each half has room. The split keeps a floor under both.
const SPLIT_ROW_MIN = 720;
const SPLIT_MIN = { column: { controls: 120, code: 160 }, row: { controls: 280, code: 280 } };

/**
 * The workspace: one preview iframe that never unmounts (switching between a
 * docs page and a canvas does not reload it), with the story chrome, the
 * panel and the overview laid around it by mode.
 */
export function StoryView({ bridge, entry, mode, state, config, onArgs, onResetArgs, onGlobals, onNavigate, urlViewport, onViewport, plugins, children }) {
  const frameRef = useRef(null);
  const [viewport, setViewport] = useState(() => viewportSelection(urlViewport).id);
  // The output slot's tab; only plugin panels give it more than Code.
  const [tab, setTab] = useState(() => (readLocal('edl-docs:tab')?.startsWith('plugin:') ? readLocal('edl-docs:tab') : 'code'));
  const [source, setSource] = useState(null);
  const [drawer, setDrawer] = useState(null); // plugin panel id open over a docs page
  const [resizing, setResizing] = useState(false);
  const [customWidth, setCustomWidth] = useState(() => viewportSelection(urlViewport).customWidth);
  const [dragging, setDragging] = useState(false);
  const [inspect, setInspect] = useState(false);
  // Each section has its own toolbar switch; the panel is open while either is.
  const [sections, setSections] = useState(readSections);
  const panelOpen = sections.controls || sections.code;
  // A section switched on or off beside the other slides in or out; while it
  // leaves, it stays rendered.
  const [slide, setSlide] = useState(null);
  const live = slide?.phase === 'leave' ? { ...sections, [slide.name]: true } : sections;
  // While the panel eases shut, it keeps showing what it showed.
  const shownRef = useRef(live);
  if (panelOpen) shownRef.current = live;
  const shown = shownRef.current;
  const [panelDock, setPanelDock] = useState(() => (readLocal('edl-docs:panel-dock') === 'bottom' ? 'bottom' : 'right'));
  const compactPanel = useMediaQuery('(max-width: 1200px)');
  const panelLayout = compactPanel ? 'bottom' : panelDock;
  // The toggle shows the choice at once. The layout waits until the panel
  // has left, then plays the arrival from the new edge.
  const [panelChoice, setPanelChoice] = useState(panelDock);
  const [panelPhase, setPanelPhase] = useState('idle');
  const [panelSize, setPanelSize] = useState(() => ({
    right: readPanelSize('right'),
    bottom: readPanelSize('bottom'),
  }));
  const [panelDragging, setPanelDragging] = useState(false);
  const panelRef = useRef(null);
  const sheetRef = useRef(null);
  const splitRef = useRef(null);
  const controlsRef = useRef(null);
  const codeRef = useRef(null);
  const sheetWidth = useElementWidth(sheetRef, mode === 'story');
  const splitAxis = panelLayout === 'bottom' && sheetWidth >= SPLIT_ROW_MIN ? 'row' : 'column';
  // Controls' share of the panel, per arrangement; null is the default fit.
  const [split, setSplit] = useState(() => ({ column: readSplit('right'), row: readSplit('bottom') }));
  const [splitDragging, setSplitDragging] = useState(false);
  const stageRef = useRef(null);
  const [paintedId, setPaintedId] = useState(null);
  const frameBoxRef = useRef(null);
  const size = useFrameSize(frameBoxRef, resizing || dragging);
  const availableWidth = useAvailableWidth(stageRef);

  useEffect(() => {
    bridge.attach(frameRef.current);
    return () => bridge.detach(frameRef.current);
  }, []);

  useEffect(
    () =>
      bridge.onMessage((msg) => {
        if (msg.type === 'rendered' && 'snippet' in msg) setSource(msg.snippet ?? null);
        if (msg.type === 'rendered' || msg.type === 'error') setPaintedId(msg.id);
        else if (msg.type === 'inspect') setInspect(Boolean(msg.on));
      }),
    [],
  );
  useEffect(() => setSource(null), [state.id]);
  useEffect(() => setResizing(false), [state.id]);
  useEffect(() => {
    if (!urlViewport?.explicit) return;
    const next = viewportSelection(urlViewport);
    setViewport(next.id);
    setCustomWidth(next.customWidth);
  }, [urlViewport?.key]);
  useEffect(() => writeLocal('edl-docs:viewport', viewport), [viewport]);
  useEffect(() => writeLocal('edl-docs:tab', tab), [tab]);
  useEffect(() => writeLocal('edl-docs:viewport-width', customWidth ?? ''), [customWidth]);
  useEffect(() => {
    const selected = viewport === 'custom' ? customWidth : VIEWPORTS.find((item) => item.id === viewport)?.width ?? null;
    onViewport(selected);
  }, [viewport, customWidth]);
  useEffect(() => {
    writeLocal('edl-docs:panel-controls', sections.controls ? '1' : '0');
    writeLocal('edl-docs:panel-code', sections.code ? '1' : '0');
  }, [sections]);
  useEffect(() => {
    writeLocal('edl-docs:panel-split-right', split.column ?? '');
    writeLocal('edl-docs:panel-split-bottom', split.row ?? '');
  }, [split]);
  useEffect(() => writeLocal('edl-docs:panel-dock', panelDock), [panelDock]);
  useEffect(() => {
    writeLocal('edl-docs:panel-size-right', panelSize.right ?? '');
    writeLocal('edl-docs:panel-size-bottom', panelSize.bottom ?? '');
  }, [panelSize]);
  useEffect(() => {
    if (mode === 'story' || panelPhase === 'idle') return;
    setPanelDock(panelChoice);
    setPanelPhase('idle');
  }, [mode, panelPhase, panelChoice]);
  // The old track eases shut, then one frame holds the new track at 0 so the
  // following open eases instead of snapping the preview width.
  useEffect(() => {
    if (panelPhase !== 'prepare') return undefined;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setPanelPhase('enter'));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [panelPhase]);
  useEffect(() => {
    if (panelPhase !== 'exit' && panelPhase !== 'enter') return undefined;
    const timer = window.setTimeout(() => {
      if (panelPhase === 'exit') {
        setPanelDock(panelChoice);
        setPanelPhase('prepare');
      } else {
        setPanelPhase('idle');
      }
    }, 480);
    return () => window.clearTimeout(timer);
  }, [panelPhase, panelChoice]);

  // Inspect lives in the iframe; the shell owns the switch and re-sends it
  // when the iframe reloads. Only a story canvas can be inspected.
  // Between a story and a docs page the frame changes shape a moment before
  // the preview has drawn the new page, and the old one (often a white story
  // canvas) would flash across it. Hide the iframe until the preview reports
  // a page of the new kind.
  const paintedKind = paintedId ? state.index.entries[paintedId]?.type : null;
  const switching = Boolean(paintedKind) && (mode === 'story' || mode === 'docs') && paintedKind !== mode;
  // A render that finishes quickly shows no loading bar or "Rendering…" (useDelayed).
  const loadingShown = useDelayed(mode === 'story' && state.status === 'loading');
  const inspecting = inspect && mode === 'story';
  useEffect(() => {
    bridge.send({ type: 'inspect', on: inspecting });
    return bridge.onMessage((msg) => msg.type === 'ready' && bridge.send({ type: 'inspect', on: inspecting }));
  }, [inspecting]);
  // The rulers stay mounted while their gutter closes, so they can leave.
  const [rulers, setRulers] = useState(false);
  // Leaving the story (for a docs page, say) drops them at once: their exit
  // only plays when Inspect is switched off over a story.
  useEffect(() => {
    if (inspecting) setRulers(true);
    else if (mode !== 'story') setRulers(false);
  }, [inspecting, mode]);

  // I toggles inspect; [ and ] step through stories.
  const stepRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      const el = document.activeElement;
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(el?.tagName ?? '') || el?.isContentEditable || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'i') setInspect((v) => !v);
      else if (e.key === 'Escape') {
        if (e.defaultPrevented) return;
        setInspect(false);
      }
      else if (e.key === '[') stepRef.current?.(-1);
      else if (e.key === ']') stepRef.current?.(1);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const previewSrc = '/preview.html';
  const viewports = customWidth && viewport === 'custom' ? [...VIEWPORTS, { id: 'custom', label: String(customWidth), width: customWidth }] : VIEWPORTS;
  const vp = viewports.find((v) => v.id === viewport) ?? VIEWPORTS[0];
  const visibleViewports = viewports.filter((item) => item.width == null || availableWidth == null || item.width <= availableWidth || item.id === vp.id);
  const story = state.story;
  const component = entry ? entry.title.split('/').pop().trim() : '';
  const docsEntry = entry ? state.index.entries[`${entry.componentId}--docs`] : null;
  const siblings = entry ? state.index.order.map((id) => state.index.entries[id]).filter((e) => e.componentId === entry.componentId && e.type === 'story') : [];
  const primaryStory = siblings[0]?.id === entry?.id;
  const docsHash = primaryStory ? '' : entry?.id ?? '';
  const docsHref = docsEntry ? `?path=/docs/${docsEntry.id}${docsHash ? `#${encodeURIComponent(docsHash)}` : ''}` : null;
  const pluginPanels = plugins.panels;
  const outputTabs = [
    { id: 'code', title: 'Code' },
    ...pluginPanels.map((p) => ({ id: `plugin:${p.id}`, title: p.title })),
  ];
  const activeTab = outputTabs.some((t) => t.id === tab) ? tab : 'code';
  // The dock switch moves to its new corner as soon as a section is switched.
  const corner = cornerOf(panelOpen ? sections : shown, splitAxis);

  // Previous / next story across the whole library, in sidebar order.
  const storyOrder = entry ? state.index.order.filter((id) => state.index.entries[id].type === 'story') : [];
  const at = entry ? storyOrder.indexOf(entry.id) : -1;
  const prev = at > 0 ? state.index.entries[storyOrder[at - 1]] : null;
  const next = at >= 0 && at < storyOrder.length - 1 ? state.index.entries[storyOrder[at + 1]] : null;
  stepRef.current = mode === 'story' ? (dir) => (dir < 0 ? prev : next) && onNavigate((dir < 0 ? prev : next).id) : null;

  // Dragging the frame's edge: the frame stays centred, so it grows by twice
  // the pointer's travel. Double-click the handle to go back to Fit.
  const startDrag = (e) => {
    const frame = frameBoxRef.current;
    const stage = frame?.parentElement;
    if (!frame || !stage) return;
    e.preventDefault();
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    const side = handle.dataset.side === 'left' ? -1 : 1;
    const startX = e.clientX;
    // Widths are the story's (the iframe's), not the frame's, which adds the
    // rulers' gutter.
    const startW = frameRef.current.offsetWidth;
    const gutter = parseFloat(getComputedStyle(frame).paddingLeft) || 0;
    const cs = getComputedStyle(stage);
    const maxW = stage.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - gutter;
    setDragging(true);
    const move = (ev) => {
      const w = Math.round(Math.min(maxW, Math.max(MIN_WIDTH, startW + side * 2 * (ev.clientX - startX))));
      setCustomWidth(w);
      setViewport('custom');
    };
    const up = () => {
      setDragging(false);
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
  };
  const fit = () => {
    setResizing(viewport !== 'fit');
    setViewport('fit');
  };
  const setDockSize = (dock, next, root) => {
    const bounds = root.getBoundingClientRect();
    const min = PANEL_MIN[dock];
    const room = dock === 'bottom' ? bounds.height - PANEL_CANVAS_MIN : bounds.width - PANEL_CANVAS_MIN;
    const max = dock === 'right' ? Math.min(PANEL_MAX_WIDTH, Math.max(min, room)) : Math.max(min, room);
    const size = Math.round(Math.min(max, Math.max(min, next)));
    setPanelSize((prev) => (prev[dock] === size ? prev : { ...prev, [dock]: size }));
  };
  const startPanelDrag = (e) => {
    if (e.button !== 0 || panelPhase !== 'idle') return;
    const root = e.currentTarget.closest('.ws');
    const panel = panelRef.current;
    if (!root || !panel) return;
    e.preventDefault();
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    const dock = panelLayout;
    const vertical = dock === 'bottom';
    const startPointer = vertical ? e.clientY : e.clientX;
    const startSize = vertical ? panel.offsetHeight : panel.offsetWidth;
    setPanelDragging(true);
    const move = (ev) => {
      const pointer = vertical ? ev.clientY : ev.clientX;
      setDockSize(dock, startSize + (startPointer - pointer), root);
    };
    const up = () => {
      setPanelDragging(false);
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
  };
  const nudgePanel = (delta) => {
    const root = panelRef.current?.closest('.ws');
    const panel = panelRef.current;
    if (!root || !panel || panelPhase !== 'idle') return;
    const current = panelLayout === 'bottom' ? panel.offsetHeight : panel.offsetWidth;
    setDockSize(panelLayout, current + delta, root);
  };
  const resetPanelSize = () => {
    setPanelSize((prev) => ({ ...prev, [panelLayout]: null }));
  };
  const movePanel = (next) => {
    if (!next || next === panelDock || panelPhase !== 'idle') return;
    setPanelChoice(next);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setPanelDock(next);
      return;
    }
    setPanelPhase('exit');
  };
  // The split between Controls and Code, as Controls' share of the panel.
  const setSplitShare = (axis, share, box) => {
    const total = axis === 'row' ? box.width : box.height;
    if (!(total > 0)) return;
    const min = SPLIT_MIN[axis];
    const low = Math.min(min.controls / total, 0.5);
    const high = Math.max(1 - min.code / total, 0.5);
    const next = Math.round(Math.min(high, Math.max(low, share)) * 1000) / 1000;
    setSplit((prev) => (prev[axis] === next ? prev : { ...prev, [axis]: next }));
  };
  const startSplitDrag = (e) => {
    const box = splitRef.current?.getBoundingClientRect();
    if (e.button !== 0 || !box) return;
    e.preventDefault();
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    const axis = splitAxis;
    setSplitDragging(true);
    const move = (ev) => setSplitShare(axis, axis === 'row' ? (ev.clientX - box.left) / box.width : (ev.clientY - box.top) / box.height, box);
    const up = () => {
      setSplitDragging(false);
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
  };
  const nudgeSplit = (delta) => {
    const box = splitRef.current?.getBoundingClientRect();
    const controls = controlsRef.current?.getBoundingClientRect();
    if (!box || !controls) return;
    const row = splitAxis === 'row';
    setSplitShare(splitAxis, ((row ? controls.width : controls.height) + delta) / (row ? box.width : box.height), box);
  };
  const resetSplit = () => setSplit((prev) => ({ ...prev, [splitAxis]: null }));
  // Beside an open section, the other slides in or out. The panel's own open
  // and close (the first section on, the last off) is the grid's ease.
  const toggleSection = (name) => {
    const other = name === 'controls' ? 'code' : 'controls';
    const slot = (name === 'controls' ? controlsRef : codeRef).current;
    const slides = panelOpen && sections[other] && !slide && !window.matchMedia('(prefers-reduced-motion: reduce), (max-width: 900px)').matches;
    setSections((prev) => ({ ...prev, [name]: !prev[name] }));
    if (!slides) {
      setSlide(null);
    } else if (sections[name]) {
      const box = slot?.getBoundingClientRect();
      setSlide(box ? { name, phase: 'leave', size: splitAxis === 'row' ? box.width : box.height } : null);
    } else {
      setSlide({ name, phase: 'enter' });
    }
  };
  // The sliding section's slot eases between nothing and its size, and the
  // other section takes the rest. The section inside keeps its full size,
  // pinned to the edge the two share, so it slides rather than squashes.
  useLayoutEffect(() => {
    if (!slide) return undefined;
    const slot = (slide.name === 'controls' ? controlsRef : codeRef).current;
    const rest = (slide.name === 'controls' ? codeRef : controlsRef).current;
    const section = slot?.firstElementChild;
    if (!slot || !rest || !section) {
      setSlide(null);
      return undefined;
    }
    const prop = splitAxis === 'row' ? 'width' : 'height';
    const from = slide.phase === 'enter' ? 0 : slide.size;
    const to = slide.phase === 'enter' ? slot.getBoundingClientRect()[prop] : 0;
    slot.dataset.slide = slide.phase;
    Object.assign(slot.style, { flex: `0 0 ${from}px`, minWidth: '0', minHeight: '0', maxHeight: 'none' });
    Object.assign(section.style, { flex: 'none', [prop]: `${Math.max(from, to)}px` });
    Object.assign(rest.style, { flex: '1 1 0', maxHeight: 'none' });
    slot.getBoundingClientRect();
    slot.style.transition = 'flex-basis var(--edl-duration-resize) var(--edl-ease-resize)';
    slot.style.flexBasis = `${to}px`;
    const finish = () => setSlide(null);
    const onEnd = (e) => e.target === slot && e.propertyName === 'flex-basis' && finish();
    slot.addEventListener('transitionend', onEnd);
    const timer = window.setTimeout(finish, 800);
    return () => {
      slot.removeEventListener('transitionend', onEnd);
      window.clearTimeout(timer);
      delete slot.dataset.slide;
      slot.style.cssText = '';
      section.style.cssText = '';
      rest.style.cssText = '';
    };
  }, [slide]);
  const onPanelLayoutEnd = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.propertyName !== 'grid-template-columns' && event.propertyName !== 'grid-template-rows') return;
    if (panelPhase === 'exit') {
      setPanelDock(panelChoice);
      setPanelPhase('prepare');
    } else if (panelPhase === 'enter') {
      setPanelPhase('idle');
    }
  };

  return (
    <div
      className={`ws ws--${mode}${mode === 'story' && !panelOpen ? ' is-panel-closed' : ''}`}
      data-panel-dock={mode === 'story' ? panelLayout : undefined}
      data-panel-motion={panelPhase !== 'idle' ? panelPhase : undefined}
      data-panel-sized={mode === 'story' && panelSize[panelLayout] ? '' : undefined}
      data-panel-dragging={panelDragging ? '' : undefined}
      style={mode === 'story' && panelSize[panelLayout] ? { '--ws-panel-size': `${panelSize[panelLayout]}px` } : undefined}
      onTransitionEnd={onPanelLayoutEnd}
    >
      {mode === 'story' && (
        <div className="ws-toolbar">
          {mode === 'story' && (docsHref ? (
            <a
              className="ws-toolbar__return"
              href={docsHref}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
                event.preventDefault();
                onNavigate({ id: docsEntry.id, hash: docsHash });
              }}
            >
              <ArrowLeft size={14} strokeWidth={1.5} aria-hidden="true" />
              Documentation
            </a>
          ) : (
            <span className="ws-toolbar__return">
              <ArrowLeft size={14} strokeWidth={1.5} aria-hidden="true" />
              Documentation
            </span>
          ))}
          <DocToolbar
            mode={mode}
            entry={entry}
            story={story}
            prompts={config.prompts}
            sections={sections}
            onToggleSection={toggleSection}
          />
        </div>
      )}

      {mode === 'story' && (
        <header className="ws-head">
          <div className="ws-head__titles">
            <div className="ws-head__title-row">
              <h1 className="ws-head__title">
                <span>{component} ·&nbsp;</span>
                <StorySelector stories={siblings} story={entry} onNavigate={onNavigate} />
              </h1>
              <DesignPill link={story?.parameters?.design} />
            </div>
          </div>
          <div className="ws-head__meta">
            <StatusLine state={state} loading={loadingShown} onRemount={() => bridge.send({ type: 'remount' })} />
          </div>
        </header>
      )}

      <div ref={stageRef} className="ws-stage" id={mode === 'story' ? 'canvas-preview' : undefined} tabIndex={mode === 'story' ? -1 : undefined}>
        {mode === 'story' && (
          <div
            className="ws-canvas-toolbar"
            style={vp.width ? { width: `calc(${vp.width}px + var(--edl-ruler) - 24px)` } : undefined}
          >
            <ToggleGroup
              className="ws-viewports"
              size="compact"
              aria-label="Viewport width"
              value={[vp.id]}
              onValueChange={(next) => {
                const id = next[0];
                if (!id) return;
                setResizing(id !== viewport);
                setViewport(id);
              }}
            >
              {visibleViewports.map((v) => (
                <ToggleGroupItem key={v.id} value={v.id} title={v.id === 'custom' ? 'Custom width (drag the canvas edge)' : undefined}>
                  {v.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <Tooltip content="Inspect (I)">
              <Button
                variant="ghost"
                size="compact"
                leadingIcon={Ruler}
                className="ws-inspect"
                aria-pressed={inspecting}
                onClick={() => setInspect((value) => !value)}
              >
                Inspect
              </Button>
            </Tooltip>
          </div>
        )}
        <div
          ref={frameBoxRef}
          className={`ws-frame${resizing ? ' is-resizing' : ''}${dragging ? ' is-dragging' : ''}${inspecting ? ' is-inspecting' : ''}${mode === 'story' ? ' has-gutter' : ''}${switching ? ' is-switching' : ''}`}
          style={mode === 'story' && vp.width ? { width: `calc(${vp.width}px + var(--edl-ruler))` } : undefined}
          onTransitionEnd={(e) => e.target === e.currentTarget && e.propertyName === 'width' && setResizing(false)}
        >
          {rulers && mode === 'story' && <Rulers bridge={bridge} open={inspecting} onClosed={() => setRulers(false)} />}
          <iframe ref={frameRef} className="ws-iframe" title={entry ? `${entry.title} preview` : 'Preview'} src={previewSrc} />
          {mode === 'story' && loadingShown && <div className="ws-loading" aria-hidden="true" />}
          {mode === 'story' &&
            ['left', 'right'].map((side) => (
              <span key={side} className={`ws-handle ws-handle--${side}`} data-side={side} onPointerDown={startDrag} onDoubleClick={fit} title="Drag to resize · double-click to fit" aria-hidden="true" />
            ))}
          {mode === 'story' && size && (
            <span className="ws-size" aria-live="off">
              {size.width} × {size.height}
            </span>
          )}
        </div>
        {/* children is an array of conditionals; [false, false] is truthy and
            an empty overlay would sit over the iframe and eat every wheel. */}
        {React.Children.toArray(children).length > 0 && <div className="ws-overlay">{children}</div>}
      </div>

      {mode === 'docs' && pluginPanels.length > 0 && (
        <>
          <div className="ws-docs-actions" hidden={Boolean(drawer)}>
            {pluginPanels.map((p) => (
              <Button key={p.id} size="compact" variant={drawer === p.id ? 'primary' : 'outlined'} className="ws-docs-action" trailingIcon={drawer === p.id ? ArrowRight : ArrowUpRight} onClick={() => setDrawer((d) => (d === p.id ? null : p.id))} aria-expanded={drawer === p.id}>
                {p.title}
              </Button>
            ))}
          </div>
          {drawer && (
            <aside className="ws-drawer" aria-label={pluginPanels.find((p) => p.id === drawer)?.title}>
              <div className="ws-drawer__head">
                <p className="edl-label">{pluginPanels.find((p) => p.id === drawer)?.title}</p>
                <Button variant="ghost" size="compact" onClick={() => setDrawer(null)}>
                  Close
                </Button>
              </div>
              <div className="ws-drawer__body edl-scroll-fade">
                <PluginPanel key={`${drawer}:${state.id}`} panel={pluginPanels.find((p) => p.id === drawer)} plugins={plugins} state={state} entry={entry} />
              </div>
            </aside>
          )}
        </>
      )}

      {mode === 'story' && (
        <aside ref={panelRef} className="ws-panel" aria-label="Canvas panel">
          <button
            type="button"
            className={`ws-panel__resize ws-panel__resize--${panelLayout}`}
            aria-label={panelLayout === 'bottom' ? 'Resize panel height' : 'Resize panel width'}
            title="Drag to resize · double-click to reset"
            onPointerDown={startPanelDrag}
            onDoubleClick={resetPanelSize}
            onKeyDown={(e) => {
              const step = e.shiftKey ? 48 : 16;
              if (panelLayout === 'right' && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
                e.preventDefault();
                nudgePanel(e.key === 'ArrowLeft' ? step : -step);
              } else if (panelLayout === 'bottom' && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
                e.preventDefault();
                nudgePanel(e.key === 'ArrowUp' ? step : -step);
              }
            }}
          />
          <div ref={sheetRef} className="ws-panel__sheet">
            <div
              ref={splitRef}
              className="ws-split"
              data-axis={splitAxis}
              data-split={split[splitAxis] != null ? '' : undefined}
              data-dragging={splitDragging ? '' : undefined}
              style={split[splitAxis] != null ? { '--ws-split': split[splitAxis] } : undefined}
            >
              {shown.controls && (
                <div ref={controlsRef} className="ws-slot ws-slot--controls">
                  <section className="ws-section" aria-labelledby="ws-section-controls">
                    <div className="ws-section__head">
                      <SlidersHorizontal className="ws-section__icon" size={14} strokeWidth={1.5} aria-hidden="true" />
                      <h2 id="ws-section-controls" className="ws-section__title">Controls</h2>
                      {story && (
                        <ControlsActions className="ws-section__actions" argTypes={story.argTypes} args={story.args} initialArgs={story.initialArgs} onChange={onArgs} onReset={onResetArgs} />
                      )}
                      {!compactPanel && corner === 'controls' && <DockToggle value={panelChoice} onChange={movePanel} />}
                    </div>
                    <ScrollArea className="ws-section__body" viewportClassName="edl-scroll-fade" contentStyle={{ width: '100%', minWidth: 0 }}>
                      {story ? (
                        <>
                          {config.ask && <DescribeState storyId={state.id} story={story} globals={state.globals} onArgs={onArgs} onGlobals={onGlobals} />}
                          <Controls compact actions={false} argTypes={story.argTypes} args={story.args} initialArgs={story.initialArgs} onChange={onArgs} onReset={onResetArgs} />
                        </>
                      ) : (
                        <p className="edl-controls__empty">Loading…</p>
                      )}
                    </ScrollArea>
                  </section>
                </div>
              )}
              {shown.controls && shown.code && (
                <button
                  type="button"
                  className="ws-split__handle"
                  aria-label={splitAxis === 'row' ? 'Resize controls and code width' : 'Resize controls and code height'}
                  title="Drag to resize · double-click to reset"
                  onPointerDown={startSplitDrag}
                  onDoubleClick={resetSplit}
                  onKeyDown={(e) => {
                    const step = e.shiftKey ? 48 : 16;
                    const keys = splitAxis === 'row' ? { ArrowLeft: -step, ArrowRight: step } : { ArrowUp: -step, ArrowDown: step };
                    if (!(e.key in keys)) return;
                    e.preventDefault();
                    nudgeSplit(keys[e.key]);
                  }}
                />
              )}
              {shown.code && (
                <div ref={codeRef} className="ws-slot ws-slot--code">
                  <section className="ws-section" aria-label="Code">
                    <OutputSection
                      tabs={outputTabs}
                      tab={activeTab}
                      onTab={setTab}
                      source={source}
                      rendered={state.status === 'rendered'}
                      tools={!compactPanel && corner === 'code' && <DockToggle value={panelChoice} onChange={movePanel} />}
                    >
                      {pluginPanels.map((p) => (
                        <TabsPanel key={p.id} value={`plugin:${p.id}`} className="ws-section__plugin">
                          <ScrollArea className="ws-section__body" viewportClassName="edl-scroll-fade" contentStyle={{ width: '100%', minWidth: 0 }}>
                            <PluginPanel panel={p} plugins={plugins} state={state} entry={entry} />
                          </ScrollArea>
                        </TabsPanel>
                      ))}
                    </OutputSection>
                  </section>
                </div>
              )}
            </div>
          </div>
        </aside>
      )}
    </div>
  );
}

/**
 * The frame's size while it is changing (a viewport switch or a drag), and
 * for a moment after, so the number can be read once it settles.
 */
/**
 * Inspect's rulers, drawn in the gutter the frame grows while inspecting so
 * they never cover the story. The preview measures and sends `rulers` each
 * frame: the eased box (in the iframe's coordinates) the rulers zero on and
 * its opacity. The ticks use the shell's foreground, because the gutter is
 * the shell page, not the story's own background.
 *
 * The gutter is always there; opening, the ticks grow in from the story's
 * edge outwards, and closing plays it backwards, then calls onClosed.
 */
const RULER_MOTION = 420;
function Rulers({ bridge, open, onClosed }) {
  const ref = useRef(null);
  const openRef = useRef(open);
  openRef.current = open;
  const closedRef = useRef(onClosed);
  closedRef.current = onClosed;
  const wake = useRef(null);
  useEffect(() => wake.current?.(), [open]);
  useEffect(() => {
    const canvas = ref.current;
    const frame = canvas.parentElement;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    let last = { rect: null, alpha: 0 };
    let p = 0;
    let raf = 0;
    let prev = 0;
    const easeOut = (t) => 1 - (1 - t) ** 3;
    const draw = () => {
      const size = parseFloat(getComputedStyle(frame).paddingLeft) || 0;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const dpr = window.devicePixelRatio || 1;
      // Reallocating the bitmap every frame of the entrance is wasted work.
      if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
      }
      const c = canvas.getContext('2d');
      c.setTransform(dpr, 0, 0, dpr, 0, 0);
      c.globalAlpha = 1;
      c.clearRect(0, 0, w, h);
      const { rect: r, alpha } = last;
      // Shell foreground. The gutter is the shell page, so the ticks follow
      // the theme instead of the story's background.
      const ink = (getComputedStyle(frame).color.match(/[\d.]+/g) ?? [245, 245, 245]).slice(0, 3).join(' ');
      // From here on, x and y are the iframe's own coordinates.
      c.translate(size, size);
      const e = easeOut(p);
      if (r && size > 1) {
        c.fillStyle = `rgb(${ink} / ${0.1 * alpha * e})`;
        c.fillRect(Math.max(r.left, 0), -size, r.right - Math.max(r.left, 0), size);
        c.fillRect(-size, Math.max(r.top, 0), size, r.bottom - Math.max(r.top, 0));
      }
      const ox = r ? r.left : 0;
      const oy = r ? r.top : 0;
      c.fillStyle = `rgb(${ink})`;
      c.font = '9px ui-monospace, Menlo, monospace';
      c.textBaseline = 'top';
      const len = (i) => (i % 8 === 0 ? 8 : i % 4 === 0 ? 5 : 3);
      // How far a tick has grown: the wave runs from the story's edge out.
      const grown = (at, span) => Math.min(1, Math.max(0, (e - (at / span) * 0.4) / 0.6));
      for (let i = -Math.floor(ox / 8); ox + i * 8 <= w - size; i++) {
        const x = Math.round(ox + i * 8);
        const g = grown(x, w);
        if (!g) continue;
        c.globalAlpha = (i % 8 === 0 ? 1 : 0.45) * g;
        c.fillRect(x, -len(i) * g, 1, len(i) * g);
        if (i % 8 === 0) c.fillText(String(i * 8), x + 3, 2 - size);
      }
      for (let i = -Math.floor(oy / 8); oy + i * 8 <= h - size; i++) {
        const y = Math.round(oy + i * 8);
        const g = grown(y, h);
        if (!g) continue;
        c.globalAlpha = (i % 8 === 0 ? 1 : 0.45) * g;
        c.fillRect(-len(i) * g, y, len(i) * g, 1);
        if (i % 8 === 0) {
          c.save();
          c.translate(2 - size, y + 3);
          c.rotate(Math.PI / 2);
          c.fillText(String(i * 8), 0, -9);
          c.restore();
        }
      }
    };
    const tick = (now) => {
      raf = 0;
      const goal = openRef.current ? 1 : 0;
      const step = reduce ? 1 : (now - prev) / RULER_MOTION;
      prev = now;
      p = goal ? Math.min(1, p + step) : Math.max(0, p - step);
      draw();
      if (p !== goal) raf = requestAnimationFrame(tick);
      else if (!goal) closedRef.current?.();
    };
    wake.current = () => {
      if (raf) return;
      prev = performance.now();
      raf = requestAnimationFrame(tick);
    };
    wake.current();
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    const off = bridge.onMessage((msg) => {
      if (msg.type === 'rulers') last = msg;
      else if (msg.type !== 'surface' && msg.type !== 'rendered') return;
      if (!raf) draw();
    });
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      off();
      wake.current = null;
    };
  }, []);
  return <canvas ref={ref} className="ws-rulers" aria-hidden="true" />;
}

function useFrameSize(ref, active) {
  const [size, setSize] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let timer;
    let armed = active;
    const ro = new ResizeObserver(([e]) => {
      if (!armed) return;
      setSize({ width: Math.round(e.contentRect.width), height: Math.round(e.contentRect.height) });
    });
    ro.observe(el);
    if (!active) timer = setTimeout(() => setSize(null), 900);
    return () => {
      armed = false;
      clearTimeout(timer);
      ro.disconnect();
    };
  }, [active]);
  return size;
}

function StorySelector({ stories, story, onNavigate }) {
  if (stories.length < 2) return <span className="ws-head__story">{story.name}</span>;
  return (
    <Dropdown
      aria-label={`Canvas: ${story.name}, change canvas`}
      value={story.id}
      options={stories.map((item) => ({ value: item.id, label: item.name }))}
      onChange={(id) => onNavigate(id, { replace: true })}
      trigger={(props) => (
        <button {...props} className="ws-head__story-trigger">
          <SwapText>{story.name}</SwapText>
          <ChevronDown size={18} strokeWidth={1.5} aria-hidden="true" />
        </button>
      )}
    />
  );
}

function StatusLine({ state, loading, onRemount }) {
  if (state.status === 'error' && state.error?.notice) return <p className="ws-status">Not pre-rendered</p>;
  if (state.status === 'error') {
    return (
      <p className="ws-status ws-status--error">
        <span className="ws-status__dot" />
        Render failed
        <Tooltip content="Remount">
          <button type="button" className="ws-status__remount" onClick={onRemount} aria-label="Remount">
            <RotateCw aria-hidden="true" />
          </button>
        </Tooltip>
      </p>
    );
  }
  if (state.status === 'loading' && loading) return <p className="ws-status">Rendering…</p>;
  return null;
}

// The panel's top-right corner holds the dock switch: the first section when
// they stack, the second when they sit side by side.
function cornerOf(shown, axis) {
  if (axis === 'row') return shown.code ? 'code' : 'controls';
  return shown.controls ? 'controls' : 'code';
}

// A rule sets it apart from the section's own actions (Reset, Copy).
function DockToggle({ value, onChange }) {
  return (
    <>
      <span className="ws-panel__rule" aria-hidden="true" />
      <ToggleGroup className="ws-panel__dock" size="compact" aria-label="Panel position" value={[value]} onValueChange={(next) => onChange(next[0])}>
        <ToggleGroupItem value="right" icon={PanelRight} aria-label="Side" />
        <ToggleGroupItem value="bottom" icon={PanelBottom} aria-label="Bottom" />
      </ToggleGroup>
    </>
  );
}

/**
 * The output half of the panel. On its own it is the code block, whose bar
 * heads the section. Plugin panels turn it into tabs: Code first.
 */
function OutputSection({ tabs, tab, onTab, source, rendered, tools, children }) {
  const tabbed = tabs.length > 1;
  const title = <h2 className="ws-section__title">Code</h2>;
  const code = source ? (
    <Code className="ws-section__code" code={source.code} language={source.language} title={tabbed ? undefined : title} tools={tabbed ? undefined : tools} />
  ) : (
    <p className="edl-controls__empty">{rendered ? 'No source for this canvas.' : 'Rendering…'}</p>
  );
  if (tabbed) {
    return (
      <Tabs className="ws-section__tabs" size="compact" value={tab} onValueChange={onTab}>
        <div className="ws-section__head">
          <TabsList>
            {tabs.map((t) => (
              <TabsTab key={t.id} value={t.id}>{t.title}</TabsTab>
            ))}
          </TabsList>
          {tools}
        </div>
        <TabsPanel value="code" className="ws-section__plugin">{code}</TabsPanel>
        {children}
      </Tabs>
    );
  }
  if (source) return code;
  return (
    <>
      <div className="ws-section__head">
        <CodeIcon className="ws-section__icon" size={14} strokeWidth={1.5} aria-hidden="true" />
        {title}
        {tools}
      </div>
      {code}
    </>
  );
}

function PluginPanel({ panel, plugins, state, entry }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!panel || !ref.current) return undefined;
    let cleanup;
    try {
      cleanup = panel.mount(ref.current, { ...plugins.api.getState(), entry });
    } catch (error) {
      console.error(`edl-docs: panel ${panel.id} failed`, error);
      ref.current.textContent = `This panel failed to load: ${error.message}`;
    }
    return () => {
      if (typeof cleanup === 'function') cleanup();
      if (ref.current) ref.current.replaceChildren();
    };
  }, [panel, state.id]);
  return <div ref={ref} className="ws-plugin" />;
}

function viewportSelection(urlViewport) {
  if (urlViewport?.explicit && Number.isFinite(urlViewport.value) && urlViewport.value > 0) {
    const preset = VIEWPORTS.find((item) => item.width === urlViewport.value);
    return preset ? { id: preset.id, customWidth: null } : { id: 'custom', customWidth: Math.max(MIN_WIDTH, Math.round(urlViewport.value)) };
  }
  const stored = readLocal('edl-docs:viewport') ?? 'fit';
  if (stored === 'custom') {
    const width = Number(readLocal('edl-docs:viewport-width'));
    if (width >= MIN_WIDTH) return { id: 'custom', customWidth: Math.round(width) };
  }
  return VIEWPORTS.some((item) => item.id === stored)
    ? { id: stored, customWidth: null }
    : { id: 'fit', customWidth: null };
}

function useAvailableWidth(ref) {
  const [width, setWidth] = useState(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const measure = () => {
      const style = getComputedStyle(element);
      const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      const ruler = parseFloat(style.getPropertyValue('--edl-ruler')) || 0;
      setWidth(Math.max(0, Math.floor(element.clientWidth - padding - ruler)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return width;
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [query]);
  return matches;
}

// The element may only exist in some modes (the panel is the canvas's), so
// observe again whenever `active` changes.
function useElementWidth(ref, active) {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    setWidth(element.clientWidth);
    const observer = new ResizeObserver(([e]) => setWidth(Math.round(e.contentRect.width)));
    observer.observe(element);
    return () => observer.disconnect();
  }, [active]);
  return width;
}

function readSections() {
  const controls = readLocal('edl-docs:panel-controls');
  const code = readLocal('edl-docs:panel-code');
  if (controls != null || code != null) return { controls: controls !== '0', code: code !== '0' };
  // Before the split, one switch opened or closed the whole panel.
  const open = readLocal('edl-docs:panel') !== '0';
  return { controls: open, code: open };
}

function readSplit(dock) {
  const n = Number(readLocal(`edl-docs:panel-split-${dock}`));
  return n > 0 && n < 1 ? n : null;
}

function readPanelSize(dock) {
  const n = Number(readLocal(`edl-docs:panel-size-${dock}`));
  if (!(n >= PANEL_MIN[dock])) return null;
  return Math.round(dock === 'right' ? Math.min(n, PANEL_MAX_WIDTH) : n);
}
function readLocal(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeLocal(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {}
}
